import * as React from "react";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import type { Department, DepartmentFormData } from "@/lib/queries/departments";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  departmentFormSchema,
  departmentsListQueryOptions,
  useDepartmentsList,
} from "@/lib/queries/departments";
import { Textarea } from "@/components/ui/textarea";
import { generateDefaultValue } from "@/lib/utils/zod";
import { api } from "@/lib/http";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  // search: z.string().optional(),
});

export const Route = createFileRoute("/_app/departments/")({
  component: RouteComponent,

  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [
      stripSearchParams({
        search: "",
      }),
    ],
  },

  loaderDeps: ({ search }) => ({
    page: search.page,
    per_page: search.per_page,
    // search: search.search,
  }),

  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(departmentsListQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "departments",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 400,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 400,
  },
  {
    id: "actions",
    header: "Actions",
    maxSize: 160,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("departments", ["update"]);
      const canDelete = useResourceAccessPermission("departments", ["delete"]);

      return (
        <Actions>
          {canEdit.data && (
            <EditDialog asChild department={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog department={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<Department>>;

function RouteComponent() {
  const searchParams = Route.useSearch();

  const departments = useDepartmentsList({
    // search: searchParams.search,
    page: searchParams.page,
    per_page: searchParams.per_page,
  });

  const canEdit = useResourceAccessPermission("departments", ["update"]);
  const canDelete = useResourceAccessPermission("departments", ["delete"]);
  const canCreate = useResourceAccessPermission("departments", ["create"]);

  const showActions = Boolean(canEdit.data || canDelete.data);

  const table = useReactTable({
    data: departments.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: departments.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Departments</h1>

        {canCreate.data && (
          <CreateDialog asChild>
            <Button>
              <PlusIcon />
              Create Department
            </Button>
          </CreateDialog>
        )}
      </div>

      {/* Threre's no searching on the backend... yet */}
      {/* <div className="mb-4">
        <Input
          placeholder="Search all columns..."
          value={search}
          onChange={(e) =>
            navigate({
              search: (old) => ({ ...old, search: e.target.value }),
            })
          }
          className="max-w-sm"
        />
      </div> */}

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={departments.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function DeleteDialog({ department }: { department: Department }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: number) => {
      return api.raw.delete(`api/v1/departments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments", "list"] });
      setOpen(false);
      toast.success(`Department "${department.name}" deleted successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-xs p-4 gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Department</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the department "{department.name}"?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(department.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  department,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; department: Department }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, ...data }: Department) => {
      return api.raw
        .put(`api/v1/departments/${id}`, { json: data })
        .json<Department>();
    },
    onSuccess: ({ name }, variables) => {
      queryClient.invalidateQueries({ queryKey: ["departments", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["departments", "detail", variables.id],
      });

      setOpen(false);
      toast.success(`Department "${name}" updated successfuly`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xs p-4 gap-3">
        <DialogHeader>
          <DialogTitle>Edit Department</DialogTitle>
        </DialogHeader>
        <DepartmentForm
          defaultValue={department}
          disabled={mutation.isPending}
          buttonText={mutation.isPending ? "Saving..." : "Update"}
          onSubmit={(data) => mutation.mutate({ id: department.id, ...data })}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      const response = await api.raw
        .post("api/v1/departments/", { json: data })
        .json<Department>();
      return response;
    },
    onSuccess: ({ name }) => {
      queryClient.invalidateQueries({ queryKey: ["departments", "list"] });
      setOpen(false);
      toast.success(`Department "${name}" created successfully`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Department</DialogTitle>
        </DialogHeader>
        <DepartmentForm
          disabled={mutation.isPending}
          buttonText={mutation.isPending ? "Saving..." : "Create"}
          onSubmit={mutation.mutate}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function DepartmentForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: DepartmentFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: DepartmentFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(departmentFormSchema),
    defaultValues: {
      ...generateDefaultValue(departmentFormSchema),
      ...defaultValue,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter department name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter department description"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}
